import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Form, Badge, ListGroup, InputGroup, Spinner, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import type { FilterSchema } from '../../types/customReport';
import apiClient from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

interface Props {
  filter: FilterSchema;
  value: any[];
  onChange: (val: any[]) => void;
  includeInactive?: boolean;
  onInactiveDetected?: () => void;
}

const MultiSelectFilter: React.FC<Props> = ({ filter, value, onChange, includeInactive = false, onInactiveDetected }) => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const { currentNursery } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<{ value: any; label: string; is_active?: boolean }[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef<{ source?: string; includeInactive?: boolean }>({});
  const onInactiveDetectedRef = useRef(onInactiveDetected);

  useEffect(() => {
    onInactiveDetectedRef.current = onInactiveDetected;
  }, [onInactiveDetected]);

  const filterKey = filter.key;
  const filterSource = filter.source;
  const filterOptions = filter.options;
  const nurseryId = currentNursery?.nursery_id;

  useEffect(() => {
    if (filterOptions) {
      const madOptions = filterOptions.map((item: string) => ({
          value: item,
          label: item,
        }));
      setOptions(madOptions);
      return;
    }
    
    if (filterSource) {
      const fetchOptions = async () => {
        const currentSource = nurseryId ? filterSource.replace('{nursery}', nurseryId).replace('/api/', '') : filterSource;
        
        // Check if we've already fetched this source with the current includeInactive state
        const alreadyFetched = fetchedRef.current.source === currentSource && fetchedRef.current.includeInactive === includeInactive;

        if (alreadyFetched) {
          // If already fetched active only, check if we need to detect inactive children
          if (!includeInactive && value && value.length > 0 && onInactiveDetectedRef.current && filterSource.includes('children') && options.length > 0) {
            const allOptionValues = options.map((o) => String(o.value));
            const hasMissing = value.some((val) => !allOptionValues.includes(String(val)));
            if (hasMissing) {
              onInactiveDetectedRef.current();
            }
          }
          return;
        }

        setLoading(true);
        try {
          let source = currentSource;
          if (includeInactive && filterSource.includes('children')) {
            const separator = source.includes('?') ? '&' : '?';
            source = `${source}${separator}all_children=1`;
          }

          const response = await apiClient.get(source);
          const rawData = response.data[filterKey] || [];
          const madOptions = rawData.map((item: any) => ({
            value: item.id,
            label: item.name,
            is_active: item.is_active_today ?? item.is_active,
          }));
          
          setOptions(madOptions);
          fetchedRef.current = { source: currentSource, includeInactive };

          // After fetching, if still in active-only mode, check for missing
          if (!includeInactive && value && value.length > 0 && onInactiveDetectedRef.current && filterSource.includes('children')) {
            const allOptionValues = madOptions.map((o: any) => String(o.value));
            const hasMissing = value.some((val) => !allOptionValues.includes(String(val)));
            if (hasMissing) {
              onInactiveDetectedRef.current();
            }
          }
        } catch (error) {
          console.error(`Failed to fetch options from ${filterSource}`, error);
        } finally {
          setLoading(false);
        }
      };
      fetchOptions();
    }
  }, [filterOptions, filterSource, nurseryId, includeInactive, value, filterKey]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isOptionSelected = (optValue: any) => {
    return value.some(v => String(v) === String(optValue));
  };

  const handleToggleOption = (opt: { value: any; label: string }) => {
    const isSelected = isOptionSelected(opt.value);
    if (isSelected) {
      onChange(value.filter((v) => String(v) !== String(opt.value)));
    } else {
      onChange([...value, opt.value]);
    }
  };

  const filteredOptions = options.filter((opt) =>
    opt?.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLabels = value
    .map((val) => options.find((o) => String(o.value) === String(val))?.label)
    .filter(Boolean);

  return (
    <div ref={containerRef} className="position-relative">
      <div
        className="form-control form-control-sm d-flex flex-wrap align-items-center cursor-pointer"
        style={{ minHeight: '31px', height: 'auto', gap: '4px' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-muted">{t('customReports.selectPlaceholder')}</span>
        ) : (
          selectedLabels.map((label, idx) => (
            <Badge
              key={idx}
              bg="primary"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', fontWeight: 400 }}
            >
              {label}
              <CloseIcon
                style={{ fontSize: '14px', marginInlineStart: '4px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const opt = options.find(o => o.label === label);
                  if (opt) handleToggleOption(opt);
                }}
              />
            </Badge>
          ))
        )}
      </div>

      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm z-index-dropdown mt-1"
          style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1050 }}
        >
          <div className="p-2 border-bottom sticky-top bg-white">
            <InputGroup size="sm">
              <InputGroup.Text className="bg-white border-end-0">
                <SearchIcon style={{ fontSize: '16px' }} />
              </InputGroup.Text>
              <Form.Control
                className="border-start-0"
                placeholder={t('customReports.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </InputGroup>
            {!loading && options.length > 0 && (
              <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 small text-decoration-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(options.map((o) => o.value));
                  }}
                  disabled={options.length > 0 && value.length === options.length}
                >
                  {t('customReports.multiSelectSelectAll')}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 small text-decoration-none text-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  disabled={value.length === 0}
                >
                  {t('customReports.multiSelectClearAll')}
                </Button>
              </div>
            )}
          </div>
          {loading ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <ListGroup variant="flush">
              {filteredOptions.length === 0 ? (
                <ListGroup.Item className="text-muted text-center py-2">{t('customReports.noOptionsFound')}</ListGroup.Item>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = isOptionSelected(opt.value);
                  const isInactive = opt.is_active === false;
                  return (
                    <ListGroup.Item
                      key={opt.value}
                      action
                      onClick={() => handleToggleOption(opt)}
                      className={`py-1 px-3 d-flex justify-content-between align-items-center ${isSelected ? 'bg-light' : ''}`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span style={isInactive ? { color: theme.palette.text.secondary } : {}}>
                        {opt.label}{isInactive ? ` (${t('common:labels.inactive')})` : ''}
                      </span>
                      {isSelected && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: theme.palette.primary.main }} />
                      )}
                    </ListGroup.Item>
                  );
                })
              )}
            </ListGroup>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
